import { useSession } from '@/features/auth/api/useSession'
import { readsCaseArchive } from '@/features/cases/caseSections'
import { CaseArchiveList } from '@/features/cases/components/CaseArchiveList'
import { CaseFiles } from '@/features/cases/components/CaseFiles'
import { CaseHistory } from '@/features/cases/components/CaseHistory'
import { StatusSendsList } from '@/features/cases/components/StatusSendsList'
import type { CaseDetail } from '@/types/domain'

/* ── Overview ─────────────────────────────────────────────────────────────── */

/** The route with the shipment's position on it, the declared cargo against
 *  what the border scale said, the boxes, the terms, the dates and who answers
 *  for them, and the profit — FR-24 read back as an even grid of six cards,
 *  one question each (S-11 revision).
 *
 *  A re-export rather than a pass-through component, for the reason the Legs
 *  and Money sections state below: this is not a slice of the composed case
 *  detail rendered by a wrapper, it is four hundred lines of route strip, cargo
 *  ledger, container list and fact grid that now live in `./overview` split
 *  across the files they belong in. The call site in CaseDetailPage — and the
 *  props it passes — are unchanged. */
export { OverviewSection } from './overview'

/* ── Legs ─────────────────────────────────────────────────────────────────── */

/** The legs the shipment is actually moving on, one card each, with the sheet
 *  that adds, edits and removes them (FR-24, FR-26, FR-52, FR-67).
 *
 *  A re-export rather than a pass-through component, for the reason the Money
 *  section states below: this is not a slice of the composed case detail
 *  rendered by a wrapper, it is three hundred lines of card, flagged place,
 *  fact grid, container strip and mutation wiring that now live in `./legs`
 *  split across the files they belong in. The call site in CaseDetailPage —
 *  and the props it passes — are unchanged. */
export { LegsSection } from './legs'

/* ── Money ────────────────────────────────────────────────────────────────── */

/** The plan against the fact, the balances that fall out of it, and the
 *  receivable series (UX-DR-17, UX-DR-18).
 *
 *  A re-export rather than a pass-through component, and the difference is the
 *  point: this section is not a slice of the composed case detail rendered by a
 *  wrapper, it is five hundred lines of table, balance cells, profit cells,
 *  tranche strip and sheet wiring that now live in `./money` split across the
 *  files they belong in. The call site in CaseDetailPage — and the props it
 *  passes — are unchanged, which is what let the split happen without touching
 *  the page's composition. */
export { MoneySection } from './money'

/* ── Client billing ─────────────────────────────────────────────────────── */

/** «Счета клиенту» (S-11 revision): the client's totals, the payment schedule,
 *  Finance's invoices and debit notes, and the payments received. It replaced
 *  `InvoicingSection`, which held the invoices alone under «Деньги». A
 *  re-export, for the Money section's reason above. */
export { BillingSection } from './money'

/* ── Documents (the `files` key) ──────────────────────────────────────────── */

/** «Документы»: the case's paper and its files (S-11).
 *
 *  Its key is `files` — the section's key before it took the paper in — and
 *  the label moved, as labels may. Two blocks, in this order:
 *
 *    · the read-only list of the paper raised on the case (`CaseArchiveList`,
 *      F2), for a holder of `document.archive.read` who is NOT given Finance's
 *      workspace — Sales, Pricing and Operations as seeded. Finance sees the
 *      same paper with its amounts under «Счета клиенту», and is not shown it
 *      twice;
 *    · the file area (Story 8.1), for everybody, with its own query, its own
 *      invalidation after an upload and a lifetime that is not the detail's.
 *
 *  Never absent: every reader on the case may read its files. */
export function FilesSection({ record }: { record: CaseDetail }) {
  const { data: user } = useSession()
  const archive = readsCaseArchive(user, record.moneyUnavailable === 'forbidden')

  return (
    <div className="flex flex-col gap-4">
      {archive ? <CaseArchiveList caseId={record.id} /> : null}
      <CaseFiles caseId={record.id} />
    </div>
  )
}

/* ── History ──────────────────────────────────────────────────────────────── */

/** Everything that has happened to this case (FR-37, UX-DR-12), in the sheet
 *  the header's «⋯» opens (S-11).
 *
 *  A pass-through, for the same reason FilesSection is one: the case's past is not a slice of the composed detail. It is TWO reads
 *  — the action log and the financial audit trail — with their own queries,
 *  their own error states and a lifetime that is not the detail's, merged into
 *  one feed by the component below. It used to render `record.history`, a field
 *  the server filled with an empty array on every response, which is how a case
 *  with a year of changes on it said "nothing has happened yet".
 *
 *  `moneyForbidden` is the record's `moneyUnavailable === "forbidden"` (story
 *  B3, fix round 1): the audit trail is the case's money, the server answers it
 *  with a 404 for that reader, and the feed is the action log alone — with no
 *  notice, because nothing failed.
 *
 *  «Отправленные статусы» (S-15, `StatusSendsList`) heads it: the feed says
 *  THAT a status went out, the journal shows WHAT the client was told. It
 *  keeps its own title under a headless host — the sheet's «История» names
 *  the whole sheet, not this card — and it has its own read, issued only while
 *  the sheet is open because the sheet mounts its body only then.
 */
export function HistorySection({
  caseId,
  moneyForbidden,
  headless = false,
}: {
  caseId: string
  moneyForbidden: boolean
  /** Drops the card's own «История» title under a host that already says it —
   *  the history sheet. */
  headless?: boolean
}) {
  return (
    <div className="flex flex-col gap-4">
      <StatusSendsList caseId={caseId} />
      <CaseHistory caseId={caseId} withAudit={!moneyForbidden} headless={headless} />
    </div>
  )
}
